import React, { useEffect, useState, useCallback } from 'react';
import { FaCheck, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { Toast as ToastType } from '../../types/notification';

interface ToastProps {
  toast: ToastType;
  onHide: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onHide }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleHide = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onHide(toast.id);
    }, 300);
  }, [toast.id, onHide]);

  useEffect(() => {
    // Enter animation
    const enterTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    // Auto hide
    const duration = toast.duration || 4000;
    if (toast.autoHide !== false && duration > 0) {
      const hideTimer = setTimeout(() => {
        handleHide();
      }, duration);

      return () => {
        clearTimeout(enterTimer);
        clearTimeout(hideTimer);
      };
    }

    return () => clearTimeout(enterTimer);
  }, [toast, handleHide]);

  const getToastConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: <FaCheck className="text-green-600" />,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          accentColor: 'bg-green-500',
        };
      case 'error':
        return {
          icon: <FaTimes className="text-red-600" />,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          accentColor: 'bg-red-500',
        };
      case 'warning':
        return {
          icon: <FaExclamationTriangle className="text-yellow-600" />,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          accentColor: 'bg-yellow-500',
        };
      case 'info':
        return {
          icon: <FaInfoCircle className="text-blue-600" />,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          accentColor: 'bg-blue-500',
        };
      default:
        return {
          icon: <FaInfoCircle className="text-gray-600" />,
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          accentColor: 'bg-gray-500',
        };
    }
  };

  const config = getToastConfig();

  return (
    <div
      className={`
        max-w-sm w-full transform transition-all duration-300 ease-in-out pointer-events-auto
        ${
          isVisible && !isLeaving
            ? 'translate-x-0 opacity-100 scale-100'
            : 'translate-x-full opacity-0 scale-95'
        }
      `}
    >
      <div
        className={`
        ${config.bgColor} ${config.borderColor} ${config.textColor}
        border rounded-lg shadow-xl p-4 relative overflow-hidden
      `}
      >
        {/* Accent line */}
        <div className={`absolute top-0 left-0 right-0 h-1 ${config.accentColor}`} />

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">{config.icon}</div>

          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm leading-5">{toast.title}</h4>
            {toast.message && <p className="text-sm opacity-90 mt-1 leading-5">{toast.message}</p>}
          </div>

          <button
            onClick={handleHide}
            className="flex-shrink-0 text-current opacity-50 hover:opacity-75 transition-opacity"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: ToastType[];
  onHideToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onHideToast }) => {
  return (
    <div className="fixed top-0 right-0 p-4 space-y-2 pointer-events-none" style={{ zIndex: 9999 }}>
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            transform: `translateY(${index * 10}px)`,
            zIndex: 9999 - index,
          }}
        >
          <Toast toast={toast} onHide={onHideToast} />
        </div>
      ))}
    </div>
  );
};

export default Toast;
